import {
  botIntervalMs,
  botProjectionMs,
  decideBot,
  initialBotMemory,
  validateBotMemory,
} from "./bot-policy.js";
import { resolvePersonality } from "./bot-seed.js";
import type {
  BotDifficulty,
  BotMemory,
  BotPersonality,
  BotSettings,
  BotTraceEntry,
} from "./bot-types.js";
import type { ConflictMatch } from "./match.js";

export interface BotRuntimeState {
  readonly seatId: string;
  readonly personality: BotPersonality;
  readonly difficulty: BotDifficulty;
  readonly sequence: number;
  readonly nextDecisionAtMs: number;
  readonly memory: BotMemory;
}

export function createBotRuntime(
  seatId: string,
  settings: BotSettings,
  rootSeed: string,
  nextDecisionAtMs = 0,
  sequence = 0,
): BotRuntimeState {
  botIntervalMs(settings.difficulty);
  if (
    !["builder", "defender", "raider", "siege-planner", "broker", "seeded-mix"].includes(
      settings.personality,
    )
  )
    throw new TypeError(`Unknown bot personality: ${settings.personality}`);
  return Object.freeze({
    seatId,
    personality: resolvePersonality(settings.personality, rootSeed, seatId),
    difficulty: settings.difficulty,
    sequence,
    nextDecisionAtMs,
    memory: initialBotMemory(),
  });
}

export function runBotDecision(
  match: ConflictMatch,
  matchId: string,
  rootSeed: string,
  state: BotRuntimeState,
): { readonly state: BotRuntimeState; readonly trace: BotTraceEntry } {
  const observation = match.observeAs(state.seatId);
  const horizonMs = botProjectionMs(state.difficulty);
  const projected = horizonMs > 0 ? match.forecastAs(state.seatId, horizonMs) : undefined;
  const decision = decideBot(observation, state.memory, {
    rootSeed,
    personality: state.personality,
    difficulty: state.difficulty,
    ...(projected ? { projected } : {}),
  });
  let sequence = state.sequence;
  let admitted = false;
  if (decision.intent.kind !== "wait") {
    const nextSequence = Math.max(sequence + 1, match.nextControllerSequence(state.seatId));
    const result = match.submitAs(state.seatId, {
      matchId,
      seatId: state.seatId,
      authorityEpoch: match.authorityEpoch(),
      controllerGeneration: match.controllerGeneration(state.seatId),
      controllerSequence: nextSequence,
      requestId: `bot:${state.seatId}:${nextSequence}`,
      expectedScopeGeneration: "0",
      intent: decision.intent,
    });
    if (result.ok) {
      admitted = true;
      sequence = nextSequence;
    }
  }
  return Object.freeze({
    state: Object.freeze({
      ...state,
      sequence,
      nextDecisionAtMs: state.nextDecisionAtMs + botIntervalMs(state.difficulty),
      memory: decision.memory,
    }),
    trace: Object.freeze({
      seatId: state.seatId,
      atGameMs: observation.gameTimeMs,
      personality: state.personality,
      difficulty: state.difficulty,
      reason: decision.reason,
      intentKind: decision.intent.kind,
      admitted,
    }),
  });
}

export function validateBotRuntime(value: unknown): BotRuntimeState {
  if (!value || typeof value !== "object" || Array.isArray(value))
    throw new TypeError("Invalid bot state");
  const state = value as BotRuntimeState;
  if (
    typeof state.seatId !== "string" ||
    !["builder", "defender", "raider", "siege-planner", "broker"].includes(state.personality) ||
    !["apprentice", "foreman", "superintendent"].includes(state.difficulty) ||
    !Number.isSafeInteger(state.nextDecisionAtMs) ||
    state.nextDecisionAtMs < 0 ||
    !Number.isSafeInteger(state.sequence) ||
    state.sequence < 0
  )
    throw new TypeError("Invalid bot state");
  validateBotMemory(state.memory);
  return Object.freeze(structuredClone(state));
}
